import React from "react";

export interface EffectsCategoryTabOption {
  value: string;
  label: string;
  count: number;
}

export interface EffectsPresetsCategoryTabsProps {
  options: EffectsCategoryTabOption[];
  value: string;
  onChange: (value: string) => void;
}

/**
 * Feature-local pill tabs for the Effects Presets browser. Unlike the shared
 * segmented `KuikaTabs`, each pill carries a category-colored dot and a preset
 * count badge and wraps across rows. The dot colors are purely presentational
 * (Kuika palette tokens) so the data layer stays color-agnostic.
 */
const CATEGORY_DOT_COLORS: Record<string, string> = {
  all: "var(--dark-5)",
  basic: "var(--blue-5)",
  hover: "var(--purple-5)",
  reveal: "var(--green-5)",
  cards: "var(--orange-5)",
  image: "var(--pink-5)",
  text: "var(--dark-7)",
  threeD: "var(--yellow-6)",
  outline: "var(--red-5)",
  utility: "var(--dark-4)",
  motion: "var(--blue-4)",
  glow: "var(--purple-4)",
  status: "var(--green-5)"
};

const EffectsPresetsCategoryTabs: React.FC<EffectsPresetsCategoryTabsProps> = ({ options, value, onChange }) => {
  return (
    <div className="effects-presets-popover__tabs" role="tablist" aria-label="Preset categories">
      {options.map((option: EffectsCategoryTabOption): React.ReactNode => {
        const isActive: boolean = option.value === value;
        // On the selected pill the fill is solid blue, so a blue-ish category
        // dot (basic) would vanish into it — force the dot white instead.
        const dotColor: string = isActive ? "var(--dark-0)" : (CATEGORY_DOT_COLORS[option.value] ?? "var(--dark-5)");
        const className: string = isActive
          ? "effects-presets-popover__tab effects-presets-popover__tab--active"
          : "effects-presets-popover__tab";
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            className={className}
            data-testid={`effects-presets-tab-${option.value}`}
            onClick={(): void => onChange(option.value)}
          >
            <span
              className="effects-presets-popover__tab-dot"
              style={{ backgroundColor: dotColor }}
              aria-hidden="true"
            />
            <span className="effects-presets-popover__tab-label">{option.label}</span>
            <span className="effects-presets-popover__tab-count">{option.count}</span>
          </button>
        );
      })}
    </div>
  );
};

export default EffectsPresetsCategoryTabs;
